import { Stack } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
  useSubmitSuccessHandler,
} from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { SideDrawer } from '@/modules/common/SideDrawer/SideDrawer';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';

import { mapFormDataToInput } from './CreateLiabilityModal.utils';
import { useCreateLiabilityMutation } from './graphql/LiabilityModal.generated';
import { LiabilityForm } from './LiabilityForm';
import { defaultValues, LiabilityFormShape } from './LiabilityModal.constants';

export interface CreateLiabilitySideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional entity the liability is associated with. */
  entityId?: string;
}

/**
 * Side-drawer counterpart to `CreateLiabilityModal` for adding a liability or
 * receivable. Mirrors `EditLiabilitySideDrawer` so add/edit read as the same
 * surface.
 */
function CreateLiabilitySideDrawerInner({
  isOpen,
  onClose,
  entityId,
}: CreateLiabilitySideDrawerProps) {
  const { householdId } = useHouseholdDetailsContext();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { formRef, handleSave } = useFormSaveHandler();
  const trackUserEvent = useTrackUserEvent();
  const { handleSubmit, reset } = useFormContext<LiabilityFormShape>();

  const [createMutation, { loading }] = useCreateLiabilityMutation({
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    onError: (error) => {
      showFeedback(FeedbackMessages.formSaveError);
      reportError(`Could not create liability`, error);
    },
  });

  const onSubmit = handleSubmit((formData) => {
    if (!householdId) return; // ts
    trackUserEvent('liability created', {
      includesPaymentData: Boolean(formData.paymentKind),
    });

    const input = mapFormDataToInput(formData, householdId);

    return createMutation({ variables: { input } });
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  useSubmitSuccessHandler(() => {
    showFeedback('Successfully created liability', { variant: 'success' });
    handleClose();
  });

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={handleClose}
      heading="Add liability or receivable"
      data-testid="create-liability-side-drawer"
      body={
        <Stack gap={2} mt={2} width="100%" flex={1}>
          <form ref={formRef} onSubmit={onSubmit} noValidate>
            <LiabilityForm entityId={entityId} />
          </form>
        </Stack>
      }
      footer={
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            loading={loading}
            disabled={loading}
          >
            Add liability or receivable
          </Button>
        </Stack>
      }
    />
  );
}

export function CreateLiabilitySideDrawer(
  props: CreateLiabilitySideDrawerProps
) {
  const formMethods = useForm<LiabilityFormShape>({ defaultValues });

  return (
    <FormProvider {...formMethods}>
      <CreateLiabilitySideDrawerInner {...props} />
    </FormProvider>
  );
}
